import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import { toast } from 'sonner';
import CosmicBackground from '../components/CosmicBackground';
import ShareCard from '../components/ShareCard';
import ShareApp from '../components/ShareApp';
import AlignmentScore from '../components/AlignmentScore';
import Logo from '../components/Logo';
import { LogOut, Sparkles, Crown, Send, Clock, Moon, Zap, Share2, Shield, Heart } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';
import MusicButton from '../components/MusicButton';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';
import TestimonialPopup from "../components/TestimonialPopup";
import { LocalNotifications } from '@capacitor/local-notifications';
const formatCountdown = (seconds) => {
  if (seconds == null || Number.isNaN(seconds)) return '';
  if (seconds <= 0) return '00:00:00';

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  return [hrs, mins, secs]
    .map((value) => String(value).padStart(2, '0'))
    .join(':');
};

const formatResetTime = (isoString) => {
  if (!isoString) return '';
  try {
    return new Date(isoString).toLocaleString();
  } catch {
    return '';
  }
};

const formatDateTime = (value, language = 'en') => {
  if (!value) return language === 'ro' ? 'Acum' : 'Now';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return language === 'ro' ? 'Acum' : 'Now';
  }

  return parsed.toLocaleString(language === 'ro' ? 'ro-RO' : 'en-US');
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  const [user, setUser] = useState(null);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dailyMessage, setDailyMessage] = useState(null);
  const [history, setHistory] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareContent, setShareContent] = useState(null);
  const [countdownSeconds, setCountdownSeconds] = useState(null);

  const trackTikTokEvent = (eventName, payload = {}) => {
    try {
      if (window.trackTikTokEvent) {
        window.trackTikTokEvent(eventName, payload);
      }
    } catch (error) {
      console.error(`TikTok ${eventName} error:`, error);
    }
  };

  const fetchDailyMessage = async (currentLanguage = language) => {
    try {
      const response = await api.get(`/universe/daily-message?language=${currentLanguage}`);
      setDailyMessage(response?.data || null);
    } catch (error) {
      console.error('Failed to fetch daily message', error);
      setDailyMessage(null);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await api.get('/universe/history');
      setHistory(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch history', error);
      setHistory([]);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await api.get('/subscription/status');
      const data = response?.data || null;

      setSubscriptionStatus(data);
      setCountdownSeconds(
        typeof data?.seconds_until_reset === 'number' ? data.seconds_until_reset : null
      );

      const localUserRaw = localStorage.getItem('user');
      if (localUserRaw && data) {
        try {
          const localUser = JSON.parse(localUserRaw);
          const updatedUser = {
            ...localUser,
            is_premium: !!data.is_premium,
            subscription_tier: data.tier === 'premium' ? 'premium' : 'free',
            subscription_status: data.is_premium
              ? (localUser.subscription_status || 'active')
              : 'inactive',
          };

          localStorage.setItem('user', JSON.stringify(updatedUser));
          setUser(updatedUser);
        } catch (parseError) {
          console.error('Failed to parse local user in subscription refresh', parseError);
        }
      }
    } catch (error) {
      console.error('Failed to fetch subscription status', error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
    } catch (error) {
      console.error('Failed to parse user from localStorage', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
      return;
    }

    fetchDailyMessage(language);
    fetchHistory();
    fetchSubscriptionStatus();
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    fetchDailyMessage(language);
  }, [language, user]);

  useEffect(() => {
    const resetStateAndRefresh = () => {
      setUpgradeLoading(false);
      setPortalLoading(false);
      setCancelLoading(false);
      fetchSubscriptionStatus();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resetStateAndRefresh();
      }
    };

    window.addEventListener('pageshow', resetStateAndRefresh);
    window.addEventListener('focus', resetStateAndRefresh);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', resetStateAndRefresh);
      window.removeEventListener('focus', resetStateAndRefresh);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (subscriptionStatus?.is_premium) {
      setUpgradeLoading(false);
    }
  }, [subscriptionStatus]);

  useEffect(() => {
    if (countdownSeconds == null) return;

    const interval = setInterval(() => {
      setCountdownSeconds((prev) => {
        if (prev == null) return null;
        if (prev <= 1) return 0;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [countdownSeconds]);

  useEffect(() => {
    if (subscriptionStatus && !subscriptionStatus.is_premium) {
      trackTikTokEvent('ViewContent', {
        content_name: billingPeriod === 'monthly'
          ? 'AskUniverse Premium Monthly'
          : 'AskUniverse Premium Annual',
        content_type: 'product',
        content_id: billingPeriod === 'monthly'
          ? 'premium_monthly'
          : 'premium_annual',
        value: billingPeriod === 'monthly' ? 9.99 : 59.0,
        currency: 'USD',
      });
    }
  }, [subscriptionStatus, billingPeriod]);

  useEffect(() => {
  const setupNotifications = async () => {
    await LocalNotifications.requestPermissions();

    await LocalNotifications.schedule({
      notifications: [
        {
          title: '✨ Cosmic Message',
          body: 'The Universe has a message for you today.',
          id: 1,
          schedule: {
            at: new Date(Date.now() + 10000),
            repeats: true,
            every: 'day'
          }
        }
      ]
    });
  };

  setupNotifications();
}, []);

  useEffect(() => {
    if (!user) return;

    const storedRevealedAnswer = sessionStorage.getItem('pendingRevealedGuestAnswer');
    if (!storedRevealedAnswer) return;

    try {
      const parsedAnswer = JSON.parse(storedRevealedAnswer);
      setAnswer(parsedAnswer);
      setQuestion('');
      sessionStorage.removeItem('pendingRevealedGuestAnswer');

      fetchHistory();

      toast.success(
        language === 'ro'
          ? 'Mesajul complet al Universului a fost dezvăluit.'
          : 'Your complete message from the Universe has been revealed.'
      );

      setTimeout(() => {
        document.getElementById('answer-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } catch (error) {
      console.error('Could not restore the revealed guest answer:', error);
      sessionStorage.removeItem('pendingRevealedGuestAnswer');
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const guestToken = sessionStorage.getItem('pendingGuestToken');
    if (!guestToken) return;

    let cancelled = false;

    const revealGuestAnswer = async () => {
      try {
        const response = await api.post('/guest-ask/reveal', {
          guest_token: guestToken,
        });

        if (cancelled) return;

        setAnswer(response?.data || null);
        setQuestion('');
        ['pendingGuestQuestion', 'pendingGuestPreview', 'pendingGuestToken', 'pendingGuestCreatedAt']
          .forEach((key) => sessionStorage.removeItem(key));

        await fetchHistory();
        toast.success(
          language === 'ro'
            ? 'Mesajul complet al Universului a fost dezvăluit.'
            : 'Your complete message from the Universe has been revealed.'
        );

        setTimeout(() => {
          document.getElementById('answer-section')?.scrollIntoView({ behavior: 'smooth' });
        }, 150);
      } catch (error) {
        if (!cancelled) {
          const message = error?.response?.data?.detail || 'Could not reveal your guest answer.';
          toast.error(message);
          if ([404, 409, 410].includes(error?.response?.status)) {
            ['pendingGuestQuestion', 'pendingGuestPreview', 'pendingGuestToken', 'pendingGuestCreatedAt']
              .forEach((key) => sessionStorage.removeItem(key));
          }
        }
      } finally {
        // Nu modificăm starea butonului Ask Universe aici.
      }
    };

    revealGuestAnswer();
    return () => { cancelled = true; };
  }, [user]);

  const handleAskQuestion = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;

    setLoading(true);
    setAnswer(null);

    try {
      const response = await api.post('/universe/ask', {
        question,
        language,
      });

      setAnswer(response?.data || null);
      setQuestion('');
      await fetchHistory();
      await fetchSubscriptionStatus();

      setTimeout(() => {
        document.getElementById('answer-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      if (error.response?.status === 429 || error.response?.status === 403) {
        toast.error(
          error?.response?.data?.detail || t('dailyLimitReached'),
          {
            action: {
              label: t('upgradeToPremium'),
              onClick: () => {
                document.getElementById('premium-upgrade-section')?.scrollIntoView({ behavior: 'smooth' });
              }
            },
            duration: 8000
          }
        );
      } else {
        toast.error(error.response?.data?.detail || 'Failed to get answer');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    setUpgradeLoading(true);

    const selectedPlan = billingPeriod === 'monthly'
      ? {
          content_name: 'AskUniverse Premium Monthly',
          content_id: 'premium_monthly',
          value: 9.99,
        }
      : {
          content_name: 'AskUniverse Premium Annual',
          content_id: 'premium_annual',
          value: 59.0,
        };

    try {
      trackTikTokEvent('AddToCart', {
        content_name: selectedPlan.content_name,
        content_type: 'product',
        content_id: selectedPlan.content_id,
        value: selectedPlan.value,
        currency: 'USD',
      });

      trackTikTokEvent('InitiateCheckout', {
        content_name: selectedPlan.content_name,
        content_type: 'product',
        content_id: selectedPlan.content_id,
        value: selectedPlan.value,
        currency: 'USD',
      });

      const origin = window.location.origin;

      const response = await api.post('/subscription/checkout', {
        tier: 'premium',
        billing_period: billingPeriod,
        origin_url: origin
      });

      if (response?.data?.url) {
        window.location.href = response.data.url;
        return;
      }

      throw new Error('No checkout URL returned');
    } catch (error) {
      console.error('Upgrade error:', error);
      toast.error(
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to initiate upgrade'
      );
      setUpgradeLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);

    try {
      const response = await api.post('/subscription/portal', {
        origin_url: window.location.origin,
      });

      if (response?.data?.url) {
        window.location.href = response.data.url;
        return;
      }

      throw new Error('No portal URL returned');
    } catch (error) {
      console.error('Portal error:', error);
      toast.error(
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to open billing portal'
      );
      setPortalLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to cancel your subscription? You will keep premium access until the end of the current billing period.'
    );

    if (!confirmed) return;

    setCancelLoading(true);

    try {
      const response = await api.post('/subscription/cancel');

      toast.success(
        response?.data?.message || 'Subscription cancellation scheduled successfully.'
      );

      const localUserRaw = localStorage.getItem('user');
      if (localUserRaw) {
        try {
          const localUser = JSON.parse(localUserRaw);
          const updatedUser = {
            ...localUser,
            subscription_status: 'cancel_at_period_end',
          };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          setUser(updatedUser);
        } catch (parseError) {
          console.error('Failed to parse local user on cancel', parseError);
        }
      }

      await fetchSubscriptionStatus();
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error(
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to cancel subscription'
      );
    } finally {
      setCancelLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout API call failed:', error);
    }

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const orbitModules = [
    {
      title: t('dreamPortal'),
      desc: t('unlockMysteries'),
      icon: Moon,
      color: 'violet',
      route: '/dream-portal',
      testId: 'dream-portal-cta',
    },
    {
      title: t('lovePortal'),
      desc: language === 'ro' ? 'Suflete pereche & timing' : 'Soulmates & timing',
      icon: Heart,
      color: 'pink',
      route: '/love-portal',
      testId: 'love-portal-cta',
    },
    {
      title: t('tarot'),
      desc: t('tarotCta'),
      icon: Sparkles,
      color: 'amber',
      route: '/tarot',
      testId: 'tarot-cta',
    },
  ];

  if (!user) return null;

  return (
    <div className="min-h-screen relative">
      <div
        className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-violet-900/20 via-slate-900 to-slate-950 pointer-events-none"
        style={{ zIndex: 1 }}
      />

      <div className="relative z-10">
        <header
          className="px-6 sm:px-12 border-b border-white/5"
          style={{
            paddingTop: 'max(env(safe-area-inset-top), 20px)',
            paddingBottom: '24px'
         }}
      >
          <div className="max-w-7xl mx-auto flex items-center justify-between flex-nowrap">
            <div className="shrink-0">
              <Logo size="md" />
            </div>
            <div className="flex shrink-0 items-center gap-4 whitespace-nowrap sm:gap-6">
              <div className="w-16 sm:w-20 h-10 flex items-center justify-center shrink-0">
                <LanguageSelector />
              </div>

              <div className="w-8 h-10 flex items-center justify-center shrink-0">
                <MusicButton />
              </div>

              <ShareApp variant="compact" showCopyButton={false} className="hidden sm:flex" />
              {user.is_admin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="flex items-center gap-2 px-3 py-1.5 bg-violet-600/20 hover:bg-violet-600/30 text-violet-400 rounded-lg transition-all text-sm font-medium"
                  data-testid="admin-panel-btn"
                >
                  <Shield size={16} />
                  <span className="hidden sm:inline">{t('adminPanel')}</span>
                </button>
              )}
              <span className="text-slate-300 text-sm hidden lg:inline" data-testid="user-name">
                {t('welcome')}, {user.full_name}
              </span>
              <button
                onClick={handleLogout}
                className="text-slate-400 hover:text-white transition-colors"
                data-testid="logout-button"
                title={t('logout')}
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </header>

        <div className="max-w-6xl mx-auto px-6 py-12 space-y-8">
          {dailyMessage && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-8 text-center"
              data-testid="daily-message-card"
            >
              <div className="flex justify-center mb-4">
                <Sparkles className="w-8 h-8 text-amber-500" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-white mb-4">
                {t('todaysCosmicMessage')}
              </h2>
              <p className="text-slate-300 text-lg leading-relaxed max-w-3xl mx-auto" data-testid="daily-message-text">
                {dailyMessage.message}
              </p>
            </motion.div>
          )}

          <AlignmentScore />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
            className="relative overflow-hidden rounded-[36px] border border-violet-500/20 bg-slate-950/80 p-3 sm:p-10"
            data-testid="orbital-dashboard"
          >
            <div className="absolute inset-0">
              <CosmicBackground />
            </div>

            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(139,92,246,0.30),transparent_46%)]" />
            <div className="absolute inset-0 bg-slate-950/25" />
            <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(2,6,23,0.22),rgba(2,6,23,0.92))]" />

            <div className="relative z-10 mx-auto h-[560px] w-full max-w-[360px] overflow-hidden sm:h-[760px] sm:max-w-[720px]">
              <div
                className="absolute rounded-full border border-violet-300/20"
                style={{ left: '50%', top: '50%', width: '300px', height: '300px', transform: 'translate(-50%, -50%)' }}
              />
              <div
                className="absolute rounded-full border border-violet-300/10"
                style={{ left: '50%', top: '50%', width: '410px', height: '410px', transform: 'translate(-50%, -50%)' }}
              />
              <div
                className="absolute h-px w-[92%] bg-gradient-to-r from-transparent via-violet-300/20 to-transparent"
                style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
              />
              <div
                className="absolute h-[78%] w-px bg-gradient-to-b from-transparent via-violet-300/15 to-transparent"
                style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
              />

              <div
                className="absolute z-20"
                style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
                onClick={() => document.getElementById('ask-universe-card')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <motion.div
                  animate={{ scale: [1, 1.035, 1] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                  className="relative flex h-36 w-36 cursor-pointer flex-col items-center justify-center overflow-hidden rounded-full border border-violet-300/40 bg-[radial-gradient(circle_at_50%_35%,rgba(236,233,255,0.94),rgba(139,92,246,0.58)_30%,rgba(30,27,75,0.98)_74%)] text-center shadow-[0_0_70px_rgba(139,92,246,0.78)] sm:h-72 sm:w-72"
                >
                  <div className="absolute inset-0 rounded-full bg-[conic-gradient(from_120deg,transparent,rgba(255,255,255,0.18),transparent,rgba(168,85,247,0.25),transparent)]" />
                  <div className="absolute inset-4 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.22),transparent_65%)] sm:inset-5" />
                  <h1 className="relative font-heading text-[16px] font-bold leading-tight tracking-wide text-white drop-shadow-lg sm:text-4xl">
                    ASK THE
                    <br />
                    UNIVERSE
                  </h1>
                  <p className="relative mt-2 text-[10px] text-violet-100 sm:mt-3 sm:text-sm">
                    Ask. Receive. Align.
                  </p>
                </motion.div>
              </div>

              {orbitModules.map((module, index) => {
                const Icon = module.icon;

                const mobilePositions = [
                  { left: '50%', top: '13%', transform: 'translate(-50%, -50%)' },
                  { left: '82%', top: '50%', transform: 'translate(-50%, -50%)' },
                  { left: '18%', top: '50%', transform: 'translate(-50%, -50%)' },
                ];

                const colorClasses = {
                  violet: 'border-violet-400/50 bg-violet-500/15 text-violet-300 shadow-[0_0_35px_rgba(139,92,246,0.45)]',
                  pink: 'border-pink-400/50 bg-pink-500/15 text-pink-300 shadow-[0_0_35px_rgba(236,72,153,0.45)]',
                  amber: 'border-amber-400/50 bg-amber-500/15 text-amber-300 shadow-[0_0_35px_rgba(245,158,11,0.45)]',
                };

                return (
                  <div
                    key={module.title}
                    className="absolute z-30 w-[112px] text-center sm:w-48"
                    style={mobilePositions[index]}
                  >
                    <motion.button
                      data-testid={module.testId}
                      onClick={() => navigate(module.route)}
                      animate={{ y: [0, -7, 0] }}
                      transition={{
                        duration: 4 + index,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                      className="group w-full"
                    >
                      <div
                        className={`mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full border backdrop-blur-xl transition-transform group-hover:scale-110 sm:mb-3 sm:h-20 sm:w-20 ${colorClasses[module.color]}`}
                      >
                        <Icon className="h-6 w-6 sm:h-9 sm:w-9" />
                      </div>

                      <h3 className="text-[13px] font-semibold leading-tight text-white sm:text-lg">
                        {module.title}
                      </h3>

                      <p className="mt-1 line-clamp-3 text-[10px] leading-tight text-slate-300 sm:text-sm">
                        {module.desc}
                      </p>
                    </motion.button>
                  </div>
                );
              })}

              <div
                className="absolute z-30 w-[130px] text-center sm:w-44"
                style={{ left: '50%', top: '86%', transform: 'translate(-50%, -50%)' }}
              >
                <motion.button
                  onClick={() => document.getElementById('premium-upgrade-section')?.scrollIntoView({ behavior: 'smooth' })}
                  animate={{ y: [0, -7, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                  className="group w-full"
                >
                  <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full border border-cyan-400/50 bg-cyan-500/15 text-cyan-300 shadow-[0_0_35px_rgba(34,211,238,0.4)] backdrop-blur-xl transition-transform group-hover:scale-110 sm:mb-3 sm:h-20 sm:w-20">
                    <Crown className="h-6 w-6 sm:h-9 sm:w-9" />
                  </div>

                  <h3 className="text-[13px] font-semibold leading-tight text-white sm:text-lg">
                    Profile & Premium
                  </h3>

                  <p className="mt-1 text-[10px] leading-tight text-slate-300 sm:text-sm">
                    Manage your plan
                  </p>
                </motion.button>
              </div>
            </div>
          </motion.div>

          {subscriptionStatus && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-card p-6"
              data-testid="subscription-card"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Crown className={subscriptionStatus.is_premium ? 'text-amber-500' : 'text-slate-500'} size={24} />
                  <div>
                    <h3 className="font-medium text-white capitalize" data-testid="subscription-tier">
                      {subscriptionStatus.is_premium ? t('premiumPlan') : 'Free Daily Access'}
                    </h3>

                    <div className="text-sm text-slate-400 space-y-1 mt-1">
                      {subscriptionStatus.is_premium ? (
                        <>
                          <p>🌌 Ask Universe: Unlimited</p>
                          <p>🔮 Tarot: Unlimited</p>
                          <p>🌙 Dream Portal: Unlimited</p>
                          <p>❤️ Love Portal: Unlimited</p>
                        </>
                      ) : (
                        <>
                          <p>✨ Daily Cosmic Message: Free</p>
                          <p>⚡ Daily Alignment Score: Free</p>
                          <p>🔒 Ask Universe, Tarot, Dream and Love Portal are Premium</p>
                        </>
                       )}
                     </div>

                    {user?.subscription_status === 'cancel_at_period_end' && (
                      <p className="text-yellow-400 text-sm mt-2">
                        Subscription cancellation is scheduled for the end of the current billing period.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {subscriptionStatus.is_premium && (
                <div className="mt-6 border border-emerald-500/40 rounded-2xl p-4 bg-emerald-500/10">
                  <p className="text-emerald-300 font-medium mb-3">Premium account active</p>

                  <div className="flex flex-col gap-3">
                    <button
                      onClick={handleManageSubscription}
                      disabled={portalLoading}
                      className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-full font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {portalLoading ? 'Opening billing portal...' : 'Manage Subscription'}
                    </button>

                    <button
                      onClick={handleCancelSubscription}
                      disabled={cancelLoading}
                      className="w-full py-3 bg-red-600 hover:bg-red-500 text-white rounded-full font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {cancelLoading ? 'Cancelling...' : 'Cancel Subscription'}
                    </button>
                  </div>

                  <p className="text-slate-400 text-xs text-center mt-3">
                    Cancel, update card, and manage billing from your account.
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {subscriptionStatus && !subscriptionStatus.is_premium && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="glass-card p-8 border-amber-500/20 relative overflow-hidden"
              data-testid="premium-upgrade-section"
              id="premium-upgrade-section"
            >
              <div className="absolute inset-0 shimmer-bg opacity-10" />

              <div className="relative z-10">
                <div className="text-center mb-6">
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center premium-glow">
                      <Crown className="w-8 h-8 text-slate-900" />
                    </div>
                  </div>
                  <h2 className="font-heading text-3xl font-bold text-gradient-gold mb-2">
                    {t('unlockPremium')}
                  </h2>
                  <p className="text-slate-400">{t('premiumDescription')}</p>
                </div>

                <div className="flex justify-center mb-8">
                  <div className="bg-slate-800/50 rounded-full p-1 flex items-center gap-1" data-testid="billing-toggle">
                    <button
                      onClick={() => setBillingPeriod('monthly')}
                      className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                        billingPeriod === 'monthly'
                          ? 'bg-violet-600 text-white'
                          : 'text-slate-400 hover:text-white'
                      }`}
                      data-testid="monthly-toggle"
                    >
                      {t('monthly')}
                    </button>
                    <button
                      onClick={() => setBillingPeriod('annual')}
                      className={`px-6 py-2 rounded-full text-sm font-medium transition-all relative ${
                        billingPeriod === 'annual'
                          ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900'
                          : 'text-slate-400 hover:text-white'
                      }`}
                      data-testid="annual-toggle"
                    >
                      {t('annual')}
                      <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-emerald-500 text-white text-xs rounded-full font-bold">
                        {t('bestValue')}
                      </span>
                    </button>
                  </div>
                </div>

                <div className="text-center mb-8">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={billingPeriod}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      {billingPeriod === 'monthly' ? (
                        <div data-testid="monthly-pricing">
                          <div className="flex items-baseline justify-center gap-1">
                            <span className="text-5xl font-bold text-white">$9.99</span>
                            <span className="text-slate-400">/{t('month')}</span>
                          </div>
                          <p className="text-slate-500 text-sm mt-2">{t('billedMonthly')}</p>
                        </div>
                      ) : (
                        <div data-testid="annual-pricing">
                          <div className="flex items-baseline justify-center gap-1">
                            <span className="text-5xl font-bold text-gradient-gold">$59.00</span>
                            <span className="text-slate-400">/{t('year')}</span>
                          </div>
                          <div className="mt-2 space-y-1">
                            <p className="text-emerald-400 font-medium flex items-center justify-center gap-2">
                              <Zap size={16} />
                              Save 50%+ vs monthly
                            </p>
                            <p className="text-slate-500 text-sm">
                              {t('equivalentTo')} <span className="text-white">$4.92</span>/{t('month')}
                            </p>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>

                <div className="mb-8">
                  <p className="text-amber-400 font-medium mb-4 flex items-center gap-2">
                    <span className="text-xl">🔥</span> {t('fullAccessTo')}
                  </p>
                  <div className="space-y-3 ml-2">
                    {[
                      t('detailedDreamInterpretations'),
                      t('personalizedTarot'),
                      t('energyReadingAlignment'),
                      'Unlimited Love Portal readings',
                      t('completeHistory'),
                      t('aiResponsePriority'),
                    ].map((benefit, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <span className="text-amber-400">•</span>
                        <span className="text-slate-300 text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleUpgrade}
                  disabled={upgradeLoading}
                  className="w-full py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 rounded-full font-bold text-lg hover:from-amber-400 hover:to-amber-500 transition-all premium-glow disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="upgrade-premium-button"
                >
                  {upgradeLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900" />
                      <span>{t('processing')}</span>
                    </>
                  ) : (
                    <>
                      <Crown size={20} />
                      <span>
                        {billingPeriod === 'monthly'
                          ? 'Activate Premium - $9.99/month'
                          : 'Activate Premium - $59/year'}
                      </span>
                    </>
                  )}
                </button>

                <div className="text-center mt-4 space-y-1">
                  <p className="text-slate-400 text-sm">{t('cancelAnytime')}</p>
                  <p className="text-slate-500 text-xs">{t('noLongTermObligations')}</p>
                </div>

                {billingPeriod === 'annual' && (
                  <p className="text-center text-slate-500 text-xs mt-2">
                    {t('annualBillingNote')}
                  </p>
                )}
              </div>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="relative overflow-hidden rounded-[28px]"
            data-testid="ask-universe-card"
            id="ask-universe-card"
          >
            <div className="absolute inset-0">
              <CosmicBackground />
            </div>

            <div className="absolute inset-0 z-[1] bg-[radial-gradient(circle_at_50%_40%,rgba(99,102,241,0.24),transparent_35%)]" />

            <div className="relative z-10 glass-card p-8 border border-violet-500/20">
              <h2 className="font-heading text-3xl font-bold text-white mb-6 text-center">
                {t('askTheUniverse')}
              </h2>

              <form onSubmit={handleAskQuestion} className="space-y-4">
                <div className="relative">
                  <textarea
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder={t('enterQuestion')}
                    className="w-full px-6 py-4 bg-slate-950/45 border border-white/10 rounded-2xl text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/50 transition-all min-h-[120px] resize-none"
                    disabled={loading}
                    data-testid="question-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || !question.trim()}
                  className="w-full py-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-full font-medium hover:from-violet-500 hover:to-purple-500 transition-all cosmic-glow disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="ask-button"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      <span>{t('consultingUniverse')}</span>
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      <span>{t('askUniverse')}</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </motion.div>

          <AnimatePresence>
            {answer && (
              <motion.div
                id="answer-section"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="glass-card p-8 border-violet-500/30"
                data-testid="answer-card"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div
                      className="w-10 h-10 rounded-full"
                      style={{
                        background: 'radial-gradient(circle at 30% 30%, #60a5fa, #3b82f6, #1d4ed8)',
                        boxShadow: '0 0 20px rgba(59, 130, 246, 0.6), 0 0 40px rgba(59, 130, 246, 0.3)'
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-heading text-xl font-bold text-gradient-gold">
                        {t('theUniverseSpeaks')}
                      </h3>
                      <button
                        onClick={() => {
                          setShareContent({
                            title: answer.question,
                            content: answer.answer,
                            date: answer.created_at,
                            type: 'question'
                          });
                          setShareModalOpen(true);
                        }}
                        className="p-2 text-slate-400 hover:text-violet-400 hover:bg-violet-500/10 rounded-lg transition-all"
                        data-testid="share-answer-btn"
                        title={t('share')}
                      >
                        <Share2 size={18} />
                      </button>
                    </div>
                    <p className="text-slate-300 leading-relaxed text-lg mb-4" data-testid="answer-text">
                      {answer.answer}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Clock size={14} />
                      <span>{formatDateTime(answer.created_at, language)}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {answer && (
            <div className="mt-6 flex justify-center">
              <TestimonialPopup feature="Ask Universe" />
            </div>
          )}

          <div className="text-center">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="text-violet-400 hover:text-violet-300 transition-colors font-medium"
              data-testid="toggle-history-button"
            >
              {showHistory ? t('hideHistory') : t('viewHistory')} {t('questionHistory')} ({history.length})
            </button>
          </div>

          <AnimatePresence>
            {showHistory && history.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-4"
                data-testid="history-section"
              >
                {history.map((item, idx) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="glass-card p-6"
                    data-testid={`history-item-${idx}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <p className="text-white font-medium flex-1">{item.question}</p>
                      <button
                        onClick={() => {
                          setShareContent({
                            title: item.question,
                            content: item.answer,
                            date: item.created_at,
                            type: 'question'
                          });
                          setShareModalOpen(true);
                        }}
                        className="p-1.5 text-slate-500 hover:text-violet-400 hover:bg-violet-500/10 rounded-lg transition-all ml-2 flex-shrink-0"
                        data-testid={`share-history-${idx}`}
                        title={t('share')}
                      >
                        <Share2 size={14} />
                      </button>
                    </div>
                    <p className="text-slate-400 text-sm mb-2">{item.answer}</p>
                    <p className="text-slate-600 text-xs">{formatDateTime(item.created_at, language)}</p>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="glass-card p-6 text-center"
          >
            <p className="text-slate-400 text-sm mb-4">{t('shareAppText')}</p>
            <ShareApp variant="pill" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="glass-card p-6 border border-red-500/20"
          >
            <h3 className="text-lg font-semibold text-white mb-2">
              {t('deleteAccountTitle')}
            </h3>

            <p className="text-slate-300 text-sm mb-3">
              {t('deleteAccountDescription')}
            </p>

            <p className="text-red-300 text-sm mb-5">
              {t('deleteAccountWarning')}
            </p>

            <button
              onClick={async () => {
                const confirmed = window.confirm(t('deleteAccountConfirmMessage'));
                if (!confirmed) return;

                try {
                  await api.delete('/auth/delete-account');

                  localStorage.removeItem('token');
                  localStorage.removeItem('user');

                  toast.success(t('deleteAccountSuccess'));
                  navigate('/');
                } catch (error) {
                  const backendMessage =
                    error?.response?.data?.detail ||
                    error?.response?.data?.message ||
                    null;

                  toast.error(backendMessage || t('deleteAccountError'));
                }
              }}
              className="px-4 py-3 rounded-lg bg-red-600 text-white hover:bg-red-500 transition-all"
            >
              {t('deleteAccount')}
            </button>
          </motion.div>
        </div>
      </div>

      <ShareCard
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        title={shareContent?.title || ''}
        content={shareContent?.content || ''}
        date={shareContent?.date || new Date().toISOString()}
        type={shareContent?.type || 'question'}
      />
    </div>
  );
};

export default Dashboard;
